/**
 * web/src/hooks/mutations/useRetryStep.ts
 * Admin-only retry. Resets a failed step to pending + resumes execution.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { qk } from '../../lib/queryKeys';
import { toast } from 'sonner';
import { retryStep } from '../../api/workflows';
import { toastApiError } from '../../lib/api';

/**
 * Retries a failed step. Both workflow.status AND step.status must be
 * 'failed' for the engine to allow the retry.
 *
 * @returns React Query mutation handle
 * @sideEffects HTTP POST; engine resumes async step execution
 * @idempotent no — increments retryCount each call
 */
export function useRetryStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { workflowId: string; stepIndex: number }) =>
      retryStep(vars.workflowId, vars.stepIndex),
    onSuccess: (_data, vars) => {
      toast.success('Step retried');
      qc.invalidateQueries({ queryKey: qk.workflow.root(vars.workflowId) });
      qc.invalidateQueries({ queryKey: qk.workflows.root() });
    },
    onError: (err) => {
      toastApiError(err, 'Retry failed');
    },
  });
}
